/* eslint-disable @nx/enforce-module-boundaries */
import { OfficerDetailFacade } from '@intrensic/shared/officer-details';
import { Injectable } from '@angular/core';
import { BaseApiService } from '@shared/data-access';
import { UploadImageService } from '@shared/feature/upload-file';
import { AppConfig } from '@shared/utils/app-config';
import { BehaviorSubject, Observable, combineLatest, map, switchMap, take, tap } from 'rxjs';
import { AgencyOfficerDetailsFacade } from '../state/agency-officer-store.facade';
import { PreviewUserHeadingServiceBase } from '@shared/feature/page-layout';
import { TranslationFromJsonService } from '@shared/feature/translate';

@Injectable({
  providedIn: 'root',
})
export class OfficerUploadImageService extends UploadImageService {
  private isDoneSubject = new BehaviorSubject<boolean>(false);
  private updatingLoggedInOfficer = false;

  constructor(
    api: BaseApiService,
    private agencyOfficerDetailsFacade: AgencyOfficerDetailsFacade,
    private officerDetailsFacade: OfficerDetailFacade,
    private appConfig: AppConfig,
    private previewUserHeadingService: PreviewUserHeadingServiceBase,
    private translationFromJsonService: TranslationFromJsonService
  ) {
    super(api);
  }

  override uploadDone(): Observable<boolean> {
    return this.isDoneSubject.asObservable();
  }

  override isLoading(): Observable<boolean> {
    return this.agencyOfficerDetailsFacade.isImageLoading();
  }

  override getImage(): Observable<string> {
    return this.agencyOfficerDetailsFacade.getAgencyOfficerDetails().pipe(map((x) => x.agencyOfficerDetails.pictureFullPath ?? this.appConfig.statics.blueLogo));
  }

  override uploadImage(image: File): void {
    this.agencyOfficerDetailsFacade.updateAgencyOfficerPhoto(image);
  }

  override uploadImageApi(image: File): Observable<string> {
    const form = new FormData();
    form.append('file', image);

    return combineLatest([this.officerDetailsFacade.getLoggedInOfficer().pipe(take(1)), this.agencyOfficerDetailsFacade.getAgencyOfficerDetails().pipe(take(1))]).pipe(
      switchMap(([loggedInOfficer, agencyOfficerDetails]) => {
        if (loggedInOfficer.uid === agencyOfficerDetails.agencyOfficerDetails.uid) {
          this.updatingLoggedInOfficer = true;
        }

        return this.api.postFormData(`api/users/${agencyOfficerDetails.agencyOfficerDetails.uid}/picture`, form).pipe(map((url) => String(url)));
      }),
      tap((x) => {
        this.isDoneSubject.next(true);
        if (this.updatingLoggedInOfficer) {
          this.previewUserHeadingService.userImageChanged(x);
          this.updatingLoggedInOfficer = false;
        }
      })
    );
  }

  override getTitle(): string {
    return this.translationFromJsonService.instant('upload-image-user-title');
  }
}
